"use client";

import { c as _c } from "react/compiler-runtime";
import { readFragment } from "@opensea/graphql";
import { BottomSheetBody, BottomSheetFooter, BottomSheetHeader, BottomSheetHeaderTitle, Flex, Form, Media, Separator, SkeletonBlock, Tooltip, tv } from "@opensea/ui-kit";
import { useLocalStorage } from "@opensea/ui-kit/hooks";
import { useMemo } from "react";
import { Controller, useFormContext, useFormState, useWatch } from "react-hook-form";
import { useTranslations } from "use-intl";
import { Button } from "../../../../../components/Button";
import { CreateOrdersSummary, CreateOrdersSummarySkeleton } from "../../../../../components/CreateOrdersSummary";
import { ExpirationSelect } from "../../../../../components/ExpirationSelect";
import { useIsFormSubmitDisabled } from "../../../../../lib/form/valid";
import { DEFAULT_EXPIRATION, MAX_NUM_TRAITS_FOR_MULTI_TRAIT_OFFERS, OFFER_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE, OFFER_EXPIRATION_LOCAL_STORAGE } from "../../../constants";
import { CreateCollectionOfferFlowTable, CreateCollectionOfferFlowTableSkeleton } from "../CollectionOfferFlowTable/CreateCollectionOfferFlowTable";
import { CreateCollectionOfferFlowHeaderActions, CreateCollectionOfferFlowHeaderActionsSkeleton } from "../CreateCollectionOfferFlowHeaderActions";
import { CreateCollectionOfferTraitInput } from "../CreateCollectionOfferTraitInput";
import { CreateCollectionOfferFormFragment } from "./CreateCollectionOfferForm.fragment";
const footerVariants = tv({
  slots: {
    buttonContainer: "grid grid-cols-2 gap-3 pb-4 md:flex md:justify-end md:pb-0"
  }
});
export const CreateCollectionOfferForm = ({
  collection: collectionKey,
  onSubmit
}) => {
  const t = useTranslations("wallet.CreateCollectionOfferForm");
  const collection = readFragment(CreateCollectionOfferFormFragment, collectionKey);
  const {
    control,
    handleSubmit
  } = useFormContext();
  const {
    errors
  } = useFormState({
    control
  });
  const offers = useWatch({
    control,
    name: "offers"
  });
  const firstOffer = offers[0];
  const [_, setOfferExpirationMinutes] = useLocalStorage(OFFER_EXPIRATION_LOCAL_STORAGE, DEFAULT_EXPIRATION);
  const [__, setOfferExpirationCustom] = useLocalStorage(OFFER_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE, undefined);
  const isFormSubmitDisabled = useIsFormSubmitDisabled(control);
  const traitOfferValidation = useMemo(() => {
    const traits = firstOffer?.traits || [];
    if (traits.length === 0) {
      return {
        isValid: true,
        reason: undefined
      };
    }

    // Check if there are multiple values for the same trait type
    const traitTypeCounts = new Map();
    for (const trait of traits) {
      const count = traitTypeCounts.get(trait.traitType) || 0;
      traitTypeCounts.set(trait.traitType, count + 1);
    }
    const hasMultipleValuesPerType = Array.from(traitTypeCounts.values()).some(count_0 => count_0 > 1);
    if (hasMultipleValuesPerType) {
      return {
        isValid: false,
        reason: t("multipleValuesPerTrait")
      };
    }

    // Check if there are too many trait types
    if (traitTypeCounts.size > MAX_NUM_TRAITS_FOR_MULTI_TRAIT_OFFERS) {
      return {
        isValid: false,
        reason: t("tooManyTraits", {
          max: MAX_NUM_TRAITS_FOR_MULTI_TRAIT_OFFERS
        })
      };
    }
    return {
      isValid: true,
      reason: undefined
    };
  }, [firstOffer?.traits, t]);
  const footer = useMemo(() => {
    return <Flex className={footerVariants().buttonContainer()}>
        <Controller control={control} name="expiration" render={({
        field
      }) => <ExpirationSelect className="w-full" customDate={field.value.customExpiration} error={errors.expiration?.customExpiration?.message} onChange={value => {
        field.onChange({
          ...field.value,
          expirationType: value
        });
        setOfferExpirationMinutes(value);
      }} onCustomDateChange={date => {
        field.onChange({
          ...field.value,
          customExpiration: date
        });
        setOfferExpirationCustom(date.toISOString());
      }} value={field.value.expirationType} />} />

        {firstOffer?.traits && firstOffer.traits.length > 0 && !traitOfferValidation.isValid ? <Tooltip content={traitOfferValidation.reason || ""}>
            <span>
              <Button disabled type="submit">
                {t("reviewTraitOffer")}
              </Button>
            </span>
          </Tooltip> : firstOffer?.trait || firstOffer?.traits && firstOffer.traits.length > 0 && traitOfferValidation.isValid ? <Button disabled={isFormSubmitDisabled} type="submit">
            {t("reviewTraitOffer")}
          </Button> : <Button disabled={isFormSubmitDisabled} type="submit">
            {t("reviewCollectionOffer")}
          </Button>}
      </Flex>;
  }, [control, isFormSubmitDisabled, firstOffer?.trait, errors.expiration?.customExpiration?.message, setOfferExpirationMinutes, setOfferExpirationCustom, t, traitOfferValidation.isValid, traitOfferValidation.reason, firstOffer?.traits?.length, firstOffer?.traits]);
  return <Form onSubmit={handleSubmit(onSubmit)}>
      <BottomSheetHeader className="justify-between">
        <BottomSheetHeaderTitle className="whitespace-nowrap">
          {firstOffer?.trait || firstOffer?.traits && firstOffer.traits.length > 0 ? t("createTraitOffer") : t("createCollectionOffer")}
        </BottomSheetHeaderTitle>
        <Media greaterThanOrEqual="md">
          <CreateCollectionOfferFlowHeaderActions />
        </Media>
      </BottomSheetHeader>

      <BottomSheetBody className="mt-2 pb-0">
        <Media lessThan="md">
          <CreateCollectionOfferFlowHeaderActions className="pb-4" />
        </Media>

        <CreateCollectionOfferFlowTable collection={collection} isReview={false} />

        <Separator className="mt-1" />

        <CreateCollectionOfferTraitInput collection={collection} traitEnabled={Boolean(collection.traitOffersEnabled)} />
        <Separator />

        <CreateOrdersSummary adjustForFees={false} className="md:w-fit" collection={collection} creatorFeeEnforced={firstOffer?.isCreatorFeeRequired} currencyIdentifier={{
        chainIdentifier: collection.chain.identifier,
        contractAddress: collection.chain.offerCurrency.address
      }} orderSide="offer" orders={offers} symbol={collection.chain.offerCurrency.symbol} />

        <Media lessThan="md">{footer}</Media>
      </BottomSheetBody>

      <Media greaterThanOrEqual="md">
        <BottomSheetFooter>{footer}</BottomSheetFooter>
      </Media>
    </Form>;
};
export const CreateCollectionOfferFormSkeleton = () => {
  const $ = _c(11);
  const t = useTranslations("wallet.CreateCollectionOfferForm");
  let t0;
  if ($[0] !== t) {
    t0 = t("createCollectionOffer");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <BottomSheetHeaderTitle>{t0}</BottomSheetHeaderTitle>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Media greaterThanOrEqual="md"><CreateCollectionOfferFlowHeaderActionsSkeleton /></Media>;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1) {
    t3 = <BottomSheetHeader className="justify-between">{t1}{t2}</BottomSheetHeader>;
    $[5] = t1;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <BottomSheetBody><Media lessThan="md"><CreateCollectionOfferFlowHeaderActionsSkeleton className="pb-4" /></Media><CreateCollectionOfferFlowTableSkeleton /><Separator className="mt-1" /><CreateOrdersSummarySkeleton orderSide="offer" /></BottomSheetBody>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <BottomSheetFooter><Flex className={footerVariants().buttonContainer()}><SkeletonBlock className="h-10 w-full rounded md:w-44" /><SkeletonBlock className="h-10 w-full rounded md:w-24" /></Flex></BottomSheetFooter>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3) {
    t6 = <>{t3}{t4}{t5}</>;
    $[9] = t3;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
};